// Apolo IDE — Design Mode context (produtor + parser compartilhados).
//
// Quando o usuário seleciona um elemento no preview (Design Mode), o composer
// injeta um bloco estruturado (seletor/classes/estilos/HTML) NA FRENTE do pedido
// — o agente recebe TODO o contexto pra editar o código-fonte. Mas na bolha do
// chat esse bloco não pode virar o "textão": ele colapsa numa PILL curta
// (ex.: `<div #header>`) seguida só do pedido do usuário. VÁRIOS elementos podem
// ser selecionados → vários blocos concatenados → várias pills.
//
// Produtor (elementToAgentPrompt / elementToLabel) e consumidor
// (parseDesignContent, usado pela ConversationFeed) vivem AQUI juntos pra os
// marcadores (HEADER/FOOTER/linha `- elemento:`) NUNCA dessincronizarem. Como o
// bloco é parte do `content` persistido, a pill sobrevive ao reload — o parser
// re-deriva os labels a partir do próprio texto.

// Marcadores estáveis. O(s) bloco(s) SEMPRE lidera(m) o content (nada prepende
// antes: serializeAttachmentsToPrompt e resolveAndEnrich só ANEXAM no fim), então
// o parser exige o HEADER no índice 0 — evita falso-positivo se o usuário citar a
// frase no meio de um pedido normal.
export const DESIGN_HEADER = 'Elemento selecionado no preview (Design Mode):';
export const DESIGN_FOOTER = 'Edite o código-fonte deste elemento conforme o pedido a seguir: ';

// Label curto pra PILL (Pedro: "apenas uma pill com nome mais simples" — não o
// monte de código). Ex.: `<div>`, `<button #tab-hermes>`.
export function elementToLabel(el) {
  if (!el) return '<elemento>';
  return `<${el.tag || 'elemento'}${el.id ? ` #${el.id}` : ''}>`;
}

// Bloco estruturado injetado no prompt (o agente recebe; a bolha esconde atrás
// da pill). Terse + estruturado: seletor + label + classes + estilos-chave + HTML.
export function elementToAgentPrompt(el) {
  if (!el) return '';
  const styles = el.computed
    ? Object.entries(el.computed)
        .filter(([, v]) => v && v !== 'none' && v !== 'normal')
        .map(([k, v]) => `${k}: ${v}`)
        .join('; ')
    : '';
  const lines = [
    DESIGN_HEADER,
    // linha explícita com o label — o parser lê ISTO (mesmo label da pill do
    // composer), então a pill na bolha bate byte-a-byte com a do composer.
    `- elemento: ${elementToLabel(el)}`,
    `- seletor: ${el.selector || '(desconhecido)'}`,
    el.classes?.length ? `- classes: ${el.classes.join(' ')}` : '',
    styles ? `- estilos: ${styles}` : '',
    el.outerHTML ? `\n\`\`\`html\n${el.outerHTML.slice(0, 1200)}\n\`\`\`` : '',
    `\n${DESIGN_FOOTER}`,
  ];
  return lines.filter(Boolean).join('\n');
}

// Extrai o label de UM bloco (linha `- elemento:`; fallback `- tag:` p/ msgs
// antigas). Puro.
function blockLabel(block) {
  // Anotação numerada (to-do do preview): a pill mostra `N · comentário`, não o
  // elemento — o comentário É o pedido do usuário (paridade Cursor browser).
  const mTodo = block.match(/^- todo (\d+):\s*(.+)$/m);
  if (mTodo) return todoToLabel(Number(mTodo[1]), mTodo[2]);
  const mNew = block.match(/^- elemento:\s*(.+)$/m);
  if (mNew) return mNew[1].trim();
  // formato antigo: `- tag: <div> #id` → `<div #id>`
  const mOld = block.match(/^- tag:\s*<([^>]+)>(?:\s*#(\S+))?/m);
  if (mOld) return `<${mOld[1]}${mOld[2] ? ` #${mOld[2]}` : ''}>`;
  return '<elemento>';
}

// ── Anotações numeradas no preview (paridade com o browser do Cursor) ─────────
// O usuário liga "Anotar" no preview, clica elementos e escreve um comentário em
// cada popup → cada anotação vira uma pill numerada no composer e UM bloco aqui.
// Vários blocos concatenados = UM prompt único no envio (o pipeline de designEls
// já concatena). O parser acima re-colapsa cada bloco numa pill `N · comentário`.

// Label curto da pill: `1 · aumenta o contraste…` (comentário truncado).
export function todoToLabel(n, comment) {
  const c = String(comment ?? '').trim().replace(/\s+/g, ' ');
  const short = c.length > 32 ? `${c.slice(0, 31)}…` : c || 'to-do';
  return `${n} · ${short}`;
}

// Bloco estruturado de UMA anotação: o comentário do popup (o pedido) + o
// contexto do elemento. Lidera com DESIGN_HEADER e fecha com DESIGN_FOOTER de
// propósito — reusa byte-a-byte os marcadores do Design Mode, então a bolha,
// o parser e o envio único já funcionam sem tocar no composer.
export function todoToAgentPrompt(el, comment, n) {
  const styles = el?.computed
    ? Object.entries(el.computed)
        .filter(([, v]) => v && v !== 'none' && v !== 'normal')
        .map(([k, v]) => `${k}: ${v}`)
        .join('; ')
    : '';
  const lines = [
    DESIGN_HEADER,
    `- todo ${n}: ${String(comment ?? '').trim()}`,
    `- elemento: ${elementToLabel(el)}`,
    `- seletor: ${el?.selector || '(desconhecido)'}`,
    el?.classes?.length ? `- classes: ${el.classes.join(' ')}` : '',
    styles ? `- estilos: ${styles}` : '',
    el?.outerHTML ? `\n\`\`\`html\n${el.outerHTML.slice(0, 1200)}\n\`\`\`` : '',
    `\n${DESIGN_FOOTER}`,
  ];
  return lines.filter(Boolean).join('\n');
}

// Parser (consumidor): dado o CONTENT persistido de uma mensagem do usuário,
// devolve { labels, userText }. Vários elementos de Design Mode podem liderar o
// texto (blocos concatenados) — devolve UM label por bloco, na ordem, e o
// userText é o que sobra depois do último bloco. Sem bloco → { labels: [],
// userText: <string original> }. Puro/testável.
//
// Degrada bem pra mensagens ANTIGAS (formato pré-`- elemento:`, só `- tag:`):
// blockLabel re-deriva o label do `- tag:` — design-turns antigos também perdem
// o textão em vez de regredir.
export function parseDesignContent(content) {
  let s = String(content ?? '');
  const labels = [];
  // consome blocos HEADER…FOOTER enquanto o texto liderar com um. Cada iteração
  // remove um bloco; o que sobra é o próximo bloco (vários elementos) ou o pedido.
  while (s.indexOf(DESIGN_HEADER) === 0) {
    const f = s.indexOf(DESIGN_FOOTER);
    if (f === -1) break; // HEADER sem FOOTER (malformado) → para, não consome
    labels.push(blockLabel(s.slice(0, f)));
    s = s.slice(f + DESIGN_FOOTER.length);
  }
  return { labels, userText: s };
}
